import React, { useState, useEffect } from 'react'
import { Box, styled } from '@mui/material'
import { Skeleton } from '@mui/material'
import { DecisionConfirmationButton } from 'components'
import {
    useRequestItemApprovalDecisions,
    useSubmitRequestItemDecision,
} from 'hooks'
import { mobileScreenWidth } from 'utils'
import { ViolatingItemDecision } from './ViolatingItemDecision'
import { useAccessRequestPolicy } from 'hooks'
import moment from 'moment'

const DecisionButtonsContainer = styled(Box)({
    flex: '1',
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '15px 30px',

    [`@media (max-width:${mobileScreenWidth})`]: {
        flexDirection: 'column',
        alignItems: 'flex-end',
        '& >span': {
            marginBottom: '12px',
        },
    },
})

export const ItemApprovalDecisionPrompt = ({ requestItem, refetch }) => {
    const {
        data: approvalDecisions,
        isLoading: isLoadingApprovalDecisions,
        refetch: refetchDecisions,
    } = useRequestItemApprovalDecisions(
        requestItem.id,
        requestItem.isApprovable,
    )
    const policy = useAccessRequestPolicy(requestItem?.requestPolicyId)

    const [decisionComment, setDecisionComment] = useState('')

    // API dates are UTC without a Z suffix; normalize so the picker
    // (moment(str) + utc().local()) shows the user's local time, matching Overview.
    const [startDate, setStartDate] = useState(
        requestItem.requestDataAssignmentStartDate
            ? moment.utc(requestItem.requestDataAssignmentStartDate).format()
            : requestItem.requestDataAssignmentStartDate,
    )
    const [endDate, setEndDate] = useState(
        requestItem.requestDataAssignmentEndDate
            ? moment.utc(requestItem.requestDataAssignmentEndDate).format()
            : requestItem.requestDataAssignmentEndDate,
    )
    const [timeConstrainedControl, setTimeConstrainedControl] = useState(false)

    const [
        submitRequestItemDecision,
        { isLoading: isSubmittingRequestItemDecision },
    ] = useSubmitRequestItemDecision(
        requestItem.businessRequestId,
        requestItem.id,
    )

    // Keep the item start date even if it is in the past; use now only when it is missing.
    useEffect(() => {
        const initialStart = requestItem.requestDataAssignmentStartDate
        if (!initialStart) {
            setStartDate(moment().format())
        }
    }, [])



    return (
        <DecisionButtonsContainer>
            {isLoadingApprovalDecisions ? (
                <Box marginX="30px">
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={192}
                        height={40}
                    />
                </Box>
            ) : requestItem.isViolatingItem ? (
                <ViolatingItemDecision
                    item={requestItem}
                    decisions={approvalDecisions}
                />
            ) : (
                approvalDecisions &&
                approvalDecisions.map((ad) => (
                    <Box component="span" key={ad.decisionId} marginX="12px">
                        <DecisionConfirmationButton
                            approvalDecision={ad}
                            decisionComment={decisionComment}
                            setDecisionComment={setDecisionComment}
                            isLoading={isSubmittingRequestItemDecision}
                            showTimeConstraintControl={
                                ad.isTimeConstraintEditable
                            }
                            requireTimeControl={ad.isTimeConstraintRequired}
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            policy={policy}
                            handleToggleValue={setTimeConstrainedControl}
                            onSuccess={() => {
                                const reqData = {
                                    id: requestItem.id,
                                    stepId: requestItem.currentApprovalStepId,
                                    decisionId: ad.decisionId,
                                    requestDataAssignmentStartDate:
                                        timeConstrainedControl
                                            ? startDate
                                            : null,
                                    requestDataAssignmentEndDate:
                                        timeConstrainedControl ? endDate : null,
                                }
                                if (decisionComment) {
                                    reqData.comment = decisionComment
                                }

                                return submitRequestItemDecision([
                                    reqData,
                                ]).then(() => {
                                    refetch()
                                    refetchDecisions()
                                })
                            }}
                        />
                    </Box>
                ))
            )}
        </DecisionButtonsContainer>
    )
}
